import clsx from 'clsx';
import Icon from 'components/Icon';
import {IconType} from 'components/Icon/types';
import useLayout from 'hooks/useLayout';
import {FC, HTMLAttributes, ReactNode, useRef} from 'react';

import {alert, danger, information, success, system} from './styles.module.css';

export type InformationType = 'alert' | 'danger' | 'success' | 'system';
export type InformationSize = 'md' | 'sm';

export type InformationProps = {
    children?: ReactNode;
    icon?: IconType;
    size?: InformationSize;
    title?: string;
    type?: InformationType;
} & HTMLAttributes<HTMLElement>;

const TYPES: Record<InformationType, string> = {
    alert,
    danger,
    success,
    system,
};

const SIZES: Record<InformationSize, string> = {
    md: 'p-6',
    sm: 'py-3 px-4',
};

const Information: FC<InformationProps> = ({
    children,
    className,
    icon,
    size = 'md',
    title,
    type = 'system',
    ...rest
}) => {
    const ref = useRef(null);
    const layout = useLayout(ref);

    if (!layout) return <div className="hidden" ref={ref} />;

    const body = (
        <aside
            className={clsx(
                'flex whitespace-pre-wrap rounded border text-base text-grey-900',
                information,
                SIZES[size],
                TYPES[type],
                className,
            )}
            ref={ref}
            {...rest}
        >
            {icon && <Icon className="mr-4" icon={icon} size="lg" />}
            <div>
                {title && <header>{title}</header>}
                {children && <span>{children}</span>}
            </div>
        </aside>
    );

    return layout === 'sideBySide' ? (
        <>
            <div>&nbsp;</div>
            <div role="gridcell">{body}</div>
        </>
    ) : (
        body
    );
};

export default Information;
